/**
 * @file ProductReviewsPage.tsx
 * @description Контент → Отзывы о товарах (phase19.10) — очередь модерации
 * клиентских отзывов: KPI, вкладки по статусам, фильтры, карточки с быстрыми
 * действиями и серверная пагинация.
 *
 * Админ не правит текст, оценку и фото отзыва — только статус, публичный
 * ответ магазина и обезличивание автора (R3 docs/business/reviews.md).
 * Ручные отзывы-«свидетельства» — соседняя страница SiteTestimonialsPage.
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router";

import { Alert } from "../../../components/ui/alert";
import { ApiError } from "../../../lib/api-client";
import { catalogProducts, type CatalogProduct } from "../../../lib/api-catalog";
import {
  productReviews,
  type AdminProductReview,
  type ProductReviewStatus,
  type ReviewCounts,
} from "../../../lib/api-product-reviews";
import {
  ReviewFilters,
  ReviewStatusTabs,
  type ReviewFiltersValue,
} from "./ProductReviewsFilters";
import { ProductReviewsKpis } from "./ProductReviewsKpis";
import { ProductReviewsList } from "./ProductReviewsList";

export function ProductReviewsPage() {
  const { slug } = useParams<{ slug: string }>();

  const [status, setStatus] = useState<ProductReviewStatus>("PENDING");
  const [filters, setFilters] = useState<ReviewFiltersValue>({
    search: "",
    productId: "",
    rating: "",
    withPhotos: false,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [items, setItems] = useState<AdminProductReview[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<ReviewCounts | null>(null);
  const [avgRating, setAvgRating] = useState<number | null>(null);
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const res = await productReviews.list({
        status,
        productId: filters.productId || undefined,
        rating: filters.rating ? Number(filters.rating) : undefined,
        withPhotos: filters.withPhotos || undefined,
        search: searchQuery.trim() || undefined,
        page,
        limit: pageSize,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить отзывы");
    } finally {
      setLoading(false);
    }
  }

  /**
   * Счётчики вкладок + средняя оценка (KPI). Среднее считает БД и отдаёт
   * вместе со счётчиками — по выборке страницы оно врало бы, как только
   * опубликованных отзывов станет больше лимита.
   */
  async function refreshSummary() {
    try {
      const data = await productReviews.counts();
      setCounts(data);
      setAvgRating(data.averageRating);
    } catch {
      setCounts(null);
      setAvgRating(null);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, filters.productId, filters.rating, filters.withPhotos, searchQuery, page, pageSize]);

  useEffect(() => {
    void refreshSummary();
    // Справочник товаров — только для селекта фильтра; без прав на каталог
    // запрос упадёт, и селект просто не показывается.
    catalogProducts
      .list()
      .then(setProducts)
      .catch(() => setProducts([]));
  }, []);

  /** Любое изменение фильтра сбрасывает пагинацию на первую страницу. */
  function patchFilters(patch: Partial<ReviewFiltersValue>) {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }

  function onModerated() {
    void refresh();
    void refreshSummary();
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="label-editorial">Контент · Модерация</div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight">Отзывы о товарах</h1>
        <p className="mt-1 text-sm text-on-surface-variant">
          Клиентские отзывы. Ручные отзывы для страниц — в разделе «Свидетельства».
        </p>
      </div>

      <ProductReviewsKpis counts={counts} avgRating={avgRating} />

      {error ? <Alert severity="error">{error}</Alert> : null}

      <ReviewStatusTabs
        value={status}
        counts={counts}
        onChange={(next) => {
          setStatus(next);
          setPage(1);
        }}
      />

      <ReviewFilters
        value={filters}
        products={products}
        onChange={patchFilters}
        onSearchSubmit={() => {
          setSearchQuery(filters.search);
          setPage(1);
        }}
      />

      <ProductReviewsList
        items={items}
        loading={loading}
        status={status}
        shopSlug={slug ?? ""}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        onPrev={() => setPage((p) => Math.max(1, p - 1))}
        onNext={() => setPage((p) => p + 1)}
        onChanged={onModerated}
      />
    </div>
  );
}
